import React, { useState } from 'react';
import { Check, CreditCard, X } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { PLAN_OPTIONS, planLabel } from '../../utils/planDisplay';
import billingService from '../../services/billingService';
import logger from '../../utils/logger';
import Modal from '../ui/Modal';

// ─────────────────────────────────────────────────────────────────────────
// ChangeSalaryDialog (Salary for subscription #312 → LIVE with the Stripe
// integration, payments cards #297-#299).
//
// Picking a paid tier redirects the whole window to Stripe Checkout; if the
// backend answers 409 already_subscribed (the user already holds a live
// subscription), we fall through to the Customer Portal instead — plan
// CHANGES happen there, where the upgrade-prorates / downgrade-at-period-end
// rules are encoded. A paid user also gets an explicit "Manage billing"
// button (portal: change card, switch plan, cancel). The FREE row is never a
// checkout target — leaving a paid tier = cancel, which lives in the portal.
//
// 🛑 NEVER wired to a plan-setting endpoint — and since 2026-10-01 there is
// none: PATCH /api/usage/users/<id>/plan was deleted, so the plan only ever
// moves when Stripe's webhook confirms a real payment event.
//
// <Modal> portals it to <body> (SettingsModal sits inside the workspace's
// transformed ancestors, #225) and, as the topmost Modal, takes Escape in the
// capture phase — so dismissing this never also closes Settings behind it.
// ─────────────────────────────────────────────────────────────────────────

const ChangeSalaryDialog = ({ current, onClose }) => {
    const { t } = useLanguage();
    const [busyPlan, setBusyPlan] = useState(null);   // plan being checked out, or '__portal__'
    const [error, setError] = useState(null);

    const currentPlan = current || 'free';
    const isPaid = currentPlan !== 'free';

    // fallbackPlan: when set and the portal answers 409 no_billing_account, we
    // fall through to a CHECKOUT for that tier. This is the ADMIN-GRANTED paid
    // plan case: the plan says "paid" but no Stripe customer/subscription exists
    // (the user never paid), so there is nothing to manage — picking a tier
    // means STARTING a subscription. The mirror image of the checkout→409
    // already_subscribed→portal fallback below.
    const openPortal = async (fallbackPlan = null) => {
        setError(null);
        setBusyPlan('__portal__');
        try {
            const { url } = await billingService.createPortal();
            window.location.assign(url);
        } catch (err) {
            if (err?.status === 409 && err?.body?.error?.type === 'no_billing_account') {
                if (fallbackPlan) {
                    setBusyPlan(null);
                    await startCheckout(fallbackPlan);
                    return;
                }
                setError(t('salaryNoBilling',
                    'No billing account yet — your current tier was granted, not purchased. Pick a tier to hire PAI.'));
                setBusyPlan(null);
                return;
            }
            logger.warn('billing portal failed', err);
            setError(t('salaryError', 'Could not start checkout — please try again.'));
            setBusyPlan(null);
        }
    };

    const startCheckout = async (plan) => {
        if (busyPlan) return;
        setError(null);
        setBusyPlan(plan);
        try {
            const { url } = await billingService.createCheckout(plan);
            window.location.assign(url);
        } catch (err) {
            // Already subscribed → the portal is where plan changes live.
            if (err?.status === 409 && err?.body?.error?.type === 'already_subscribed') {
                await openPortal();
                return;
            }
            logger.warn('billing checkout failed', err);
            setError(t('salaryError', 'Could not start checkout — please try again.'));
            setBusyPlan(null);
        }
    };

    const pick = (value) => {
        if (busyPlan) return;
        if (value === currentPlan) return;                 // no-op: already on it
        if (value === 'free') {
            // Leaving a paid tier = cancelling — that lives in the portal.
            // (No billing account → the teaching error: a granted tier has no
            // subscription to cancel; only an admin can move it to free.)
            if (isPaid) openPortal();
            return;
        }
        if (isPaid) {
            // Plan CHANGE: portal (encodes prorate-up / schedule-down rules) —
            // unless nothing was ever purchased, in which case the clicked tier
            // starts a real subscription via checkout.
            openPortal(value);
            return;
        }
        startCheckout(value);
    };

    return (
        <Modal
            onClose={onClose}
            zIndex="z-[80]"
            size="sm"
            surface="bg-white/[0.03] border border-pai-subtle rounded-xl shadow-modal"
            cardClassName="min-w-0"
            cardTestId="change-salary-dialog"
            ariaLabel={t('changeSalary', 'Change Salary')}
        >
                <header className="flex items-start justify-between gap-2 px-4 pt-3 pb-2 border-b border-gray-100 border-pai-subtle">
                    <span className="text-sm font-semibold text-white truncate">
                        {t('changeSalary', 'Change Salary')}
                    </span>
                    <button
                        type="button"
                        data-testid="change-salary-close"
                        onClick={onClose}
                        aria-label={t('close', 'Close')}
                        className="flex-shrink-0 p-1 rounded text-pai-faint hover:text-white hover:bg-white/[0.07]"
                    >
                        <X size={16} />
                    </button>
                </header>

                <ul className="px-4 py-2 min-w-0">
                    {PLAN_OPTIONS.map(({ value }) => {
                        const active = value === currentPlan;
                        const busy = busyPlan === value;
                        const clickable = !active && !(value === 'free' && !isPaid);
                        return (
                            <li key={value} className="mb-1.5 last:mb-0">
                                <button
                                    type="button"
                                    data-testid="change-salary-option"
                                    data-plan={value}
                                    data-current={active ? 'true' : 'false'}
                                    data-busy={busy ? 'true' : 'false'}
                                    disabled={!clickable || !!busyPlan}
                                    onClick={() => pick(value)}
                                    className={`w-full flex items-center justify-between gap-3 px-2 py-2 rounded-lg border min-w-0 text-left ${
                                        active
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                            : 'border-pai-subtle'
                                    } ${clickable && !busyPlan
                                        ? 'hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 cursor-pointer'
                                        : 'cursor-default'} ${busyPlan && !busy ? 'opacity-60' : ''}`}
                                >
                                    <span className="text-sm text-white truncate min-w-0">
                                        {t(`plan_${value}`, planLabel(value))}
                                    </span>
                                    {active && (
                                        <span className="flex items-center gap-1 text-[11px] font-semibold text-blue-700 dark:text-blue-300 flex-shrink-0">
                                            <Check size={12} />
                                            {t('salaryCurrent', 'Current')}
                                        </span>
                                    )}
                                    {busy && (
                                        <span className="text-[11px] text-pai-meta flex-shrink-0">
                                            {t('salaryRedirecting', 'Redirecting to secure checkout…')}
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {isPaid && (
                    <div className="px-4 pb-1">
                        <button
                            type="button"
                            data-testid="change-salary-manage"
                            disabled={!!busyPlan}
                            onClick={openPortal}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-pai text-sm text-pai-secondary hover:bg-white/[0.07] min-w-0"
                        >
                            <CreditCard size={14} className="flex-shrink-0" />
                            <span className="truncate">{t('salaryManage', 'Manage billing')}</span>
                        </button>
                    </div>
                )}

                {error && (
                    <p
                        data-testid="change-salary-error"
                        className="px-4 pb-1 text-[11px] text-red-600 dark:text-red-400 break-words"
                    >
                        {error}
                    </p>
                )}

                <p
                    data-testid="change-salary-note"
                    className="px-4 pb-3 text-[11px] text-pai-meta break-words"
                >
                    {t(
                        'changeSalaryNote',
                        'You will be redirected to our secure Stripe checkout. Prices exclude VAT — tax is added at checkout.',
                    )}
                </p>
            </Modal>
    );
};

export default ChangeSalaryDialog;
